import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DirectorySettingsService, type DirectoryConfig } from '../directory-settings.service';
import {
  AccordionComponent,
  AccordionItemComponent,
  Checkbox,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  FormField,
  HelpDialogService,
  Input,
  LoadingStateComponent,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  ToastService,
  TooltipDirective,
  ZagButton,
  ZagIcon,
} from '@zag/ui';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, catchError, of } from 'rxjs';

@Component({
  selector: 'sc-directory-settings',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    AccordionComponent,
    AccordionItemComponent,
    FormField,
    Input,
    Checkbox,
    ZagButton,
    ZagIcon,
    TooltipDirective,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './directory-settings.component.html',
  styleUrl: './directory-settings.component.scss',
})
export class DirectorySettingsComponent {
  private readonly fb = inject(FormBuilder);
  private readonly directorySettings = inject(DirectorySettingsService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly helpDialog = inject(HelpDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly isSaving = signal(false);
  protected readonly isTesting = signal(false);
  protected readonly testResult = signal<{ success: boolean; message: string } | null>(null);
  protected readonly secretPlaceholder = signal('');

  protected readonly profileName = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.directory.title');
  });
  protected readonly profileSubtitle = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.directory.description');
  });

  protected readonly settingsForm = this.fb.group({
    microsoftEnabled: [false],
    microsoftTenantId: [''],
    microsoftClientId: [''],
    microsoftClientSecret: [''],
  });

  protected readonly configResource = rxResource<DirectoryConfig | null, void>({
    stream: () =>
      this.directorySettings.getConfig().pipe(
        map(response => {
          this.populateForm(response);
          return response;
        }),
        catchError(() => {
          this.toast.error(this.translate.instant('settings.directory.loadError'));
          return of(null);
        })
      ),
  });

  private populateForm(config: DirectoryConfig): void {
    const ms = config.microsoft;
    this.settingsForm.patchValue({
      microsoftEnabled: ms?.enabled ?? false,
      microsoftTenantId: ms?.tenantId ?? '',
      microsoftClientId: ms?.clientId ?? '',
      microsoftClientSecret: '',
    });
    this.secretPlaceholder.set((ms as { hasClientSecret?: boolean } | null)?.hasClientSecret ? '••••••••' : '');
  }

  protected onSave(): void {
    if (!this.settingsForm.valid) return;
    this.isSaving.set(true);

    const v = this.settingsForm.value;

    // Send clientSecret only when the user typed a new one.
    // null / omitting it tells the backend to preserve the existing secret.
    const newSecret = v.microsoftClientSecret?.trim() || null;

    const payload: DirectoryConfig = {
      microsoft: {
        enabled: v.microsoftEnabled ?? false,
        tenantId: v.microsoftTenantId || undefined,
        clientId: v.microsoftClientId || undefined,
        ...(newSecret ? { clientSecret: newSecret } : {}),
      },
    };

    this.directorySettings
      .patchConfig(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.isSaving.set(false);
          this.toast.success(this.translate.instant('settings.directory.saveSuccess'));
          this.populateForm(response);
        },
        error: () => {
          this.isSaving.set(false);
          this.toast.error(this.translate.instant('settings.directory.saveError'));
        },
      });
  }

  protected openMicrosoftHelp(): void {
    this.helpDialog.openTopic('directory.microsoft');
  }

  protected openGoogleHelp(): void {
    this.helpDialog.openTopic('directory.google');
  }

  protected onTestConnection(): void {
    this.isTesting.set(true);
    this.testResult.set(null);
    this.directorySettings
      .testConnection({ provider: 'microsoft' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isTesting.set(false);
          // Use the translated success message; for failures the server provides
          // a diagnostic string that helps the admin diagnose the issue.
          this.testResult.set({
            success: result.success,
            message: result.success
              ? this.translate.instant('settings.directory.test.success')
              : (result.message ?? this.translate.instant('settings.directory.test.error')),
          });
        },
        error: error => {
          this.isTesting.set(false);
          const message = (error?.error?.message as string | undefined) ?? this.translate.instant('settings.directory.test.error');
          this.testResult.set({ success: false, message });
        },
      });
  }
}
